import { useCallback } from 'react';
import { ChartExportRenderer } from 'shared/chart/core/types';
import { renderToSvgElement } from 'shared/chart/shared/render';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn as createStore } from 'zustand/traditional';

export const DefaultChartExportWidthPx = 1200;
export const DefaultChartExportHeightPx = 400;

export type TrendExportChartType =
    | 'time-series'
    | 'frequency-spectrum'
    | 'time-domain'
    | 'frequency-waterfall';

export type TrendChartExportInfo = Readonly<{
    chartType: TrendExportChartType;
    renderChart: ChartExportRenderer;
}>;

type ChartExportStore = Readonly<{
    chartInfo: Partial<Record<TrendExportChartType, TrendChartExportInfo>>;
    registerChartInfo: (info: TrendChartExportInfo) => () => void;
    getChartInfo: (chartType: TrendExportChartType) => TrendChartExportInfo | undefined;
}>;

const useChartExportStore = createStore<ChartExportStore>(
    (set, get) => ({
        chartInfo: {},
        registerChartInfo: (info) => {
            set(({ chartInfo }) => ({
                chartInfo: { ...chartInfo, [info.chartType]: info },
            }));

            return () =>
                set(({ chartInfo }) => ({
                    chartInfo: { ...chartInfo, [info.chartType]: undefined },
                }));
        },
        getChartInfo: (chartType) => get().chartInfo[chartType],
    }),
    shallow,
);

export function useTrendChartExporter() {
    const { registerChartInfo, getChartInfo } = useChartExportStore(
        (state) => ({
            registerChartInfo: state.registerChartInfo,
            getChartInfo: state.getChartInfo,
        }),
        shallow,
    );

    const generateChartSVG = useCallback(
        async (
            chartType: TrendExportChartType,
            chartWidth = DefaultChartExportWidthPx,
            chartHeight = DefaultChartExportHeightPx,
        ): Promise<SVGSVGElement> => {
            const info = getChartInfo(chartType);

            if (!info) {
                throw new Error(`${chartType} chart not registered`);
            }

            return renderToSvgElement(info.renderChart, chartWidth, chartHeight);
        },
        [getChartInfo],
    );

    return { registerChartInfo, generateChartSVG };
}
